import { Accordion, Skeleton, Text } from '@mantine/core'
import { ReactQuery } from 'cms-api'
import Link from 'next/link'
import React from 'react'
import { EMAILS, formatPrice, ROUTE } from 'shared-constants'
import { Button, BUTTON_VARIANT } from 'components/Button'
import { Card, CARD_ELEVATION, CARD_PADDING_SIZE } from 'components/Card'
import Icon from 'components/Icon/Icon'
import { Box, Flex, Grid } from 'components/Layout'
import { StyledLink } from 'components/Link'
import NoData from 'components/NoData'
import { STATUS_BADGE_TYPE, StatusBadge } from 'components/StatusBadge'
import { COLOR_NAMES, LAYOUT_WRAPPER_MAX_WIDTH, SPACE } from 'Theme'
import { DeliveryProgress } from './DeliveryProgress'

const RecentOrdersHeaderSkeleton = () => (
  <Flex
    justifyContent="space-between"
    alignItems="center"
    p={SPACE.PX_30}
    borderBottom={`1px solid ${COLOR_NAMES.JUPITER}`}
  >
    <Skeleton height={22} width={160} />
    <Skeleton height={36} width={140} radius="md" />
  </Flex>
)

const RecentOrderRowSkeleton = () => (
  <Flex
    p="30px"
    justifyContent="space-between"
    alignItems="center"
    borderTop={`1px solid ${COLOR_NAMES.JUPITER}`}
  >
    <Flex>
      <Box width="230px">
        <Skeleton height={22} width={120} />
      </Box>
      <Box width="230px">
        <Skeleton height={18} width={160} />
      </Box>
      <Skeleton height={14} width={120} />
    </Flex>

    <Skeleton height={16} width={60} />
  </Flex>
)

const InfoRow = ({
  label,
  value,
}: {
  label: string
  value: string | number
}) => (
  <Box>
    <Text fz={14} c={COLOR_NAMES.CARBON_COPY}>
      {label}
    </Text>
    <Text fw={500} c="#2C3E50" fz={{ base: 14, xl: 16 }} mt={2}>
      {value}
    </Text>
  </Box>
)

const BRAND_COLORS: Record<string, string> = {}
const BRAND_COLOR_PALETTE = [
  '#5872BE',
  '#C05621',
  '#2A4365',
  '#553C9A',
  '#744210',
  '#97266D',
  '#285E61',
]
const THE_EXPERT_COLOR = '#126554'

function getBrandColor(brand: string): string {
  if (!brand || brand === 'The Expert') {
    return THE_EXPERT_COLOR
  }
  if (!BRAND_COLORS[brand]) {
    const hash = brand
      .split('')
      .reduce((acc, char) => acc + char.charCodeAt(0), 0)
    BRAND_COLORS[brand] = BRAND_COLOR_PALETTE[hash % BRAND_COLOR_PALETTE.length]
  }
  return BRAND_COLORS[brand]
}

const RecentOrder = ({ order }: { order: ReactQuery.ExpertDashboardOrder }) => {
  const isTrade = order.type === 'Trade'
  const brandLabel = isTrade ? (order.brand ?? order.type) : 'The Expert'
  const brandColor = getBrandColor(brandLabel)

  return (
    <Accordion.Item key={order.id} value={order.id.toString()}>
      <Accordion.Control style={{ border: 'none', padding: 0 }}>
        <Flex
          p={{ MOBILE: '16px', TABLET: '30px' }}
          justifyContent="space-between"
          alignItems="center"
          flexDirection={{ MOBILE: 'column', TABLET: 'row' }}
          // borderTop={`1px solid ${COLOR_NAMES.JUPITER}`}
        >
          <Flex
            flexDirection={{ MOBILE: 'column', TABLET: 'row' }}
            gap={10}
            width="100%"
          >
            <Flex justifyContent="space-between" width="100%">
              <Box width={{ MOBILE: 'auto', sm: '230px' }}>
                {isTrade ? (
                  <span
                    style={{
                      backgroundColor: brandColor,
                      color: '#fff',
                      fontSize: 10,
                      fontWeight: 500,
                      padding: '2px 7px',
                      borderRadius: 4,
                      display: 'inline-block',
                      whiteSpace: 'nowrap',
                      letterSpacing: '0.3px',
                    }}
                  >
                    {brandLabel}
                  </span>
                ) : (
                  <StatusBadge
                    type={
                      order.type === 'Custom'
                        ? STATUS_BADGE_TYPE.CLIENT_PURCHASE
                        : STATUS_BADGE_TYPE.ORDER
                    }
                    status={order.status.toUpperCase()}
                  />
                )}
              </Box>
              <Text
                fz={14}
                color={COLOR_NAMES.CARBON_COPY}
                display={{ base: 'block', sm: 'none' }}
                mb={8}
              >
                {new Date(order.orderDate).toLocaleDateString('en-US')}
              </Text>
            </Flex>
            <Flex justifyContent="space-between" width="100%">
              <Box width={{ MOBILE: 'auto', sm: '230px' }}>
                <Text fw={500} c={COLOR_NAMES.EEIRE_BLACK}>
                  {isTrade
                    ? order.name
                    : `${order.type === 'Custom' ? 'Custom ' : ''}Order #${order.id}`}
                </Text>
              </Box>
              <Box display={{ MOBILE: 'block', TABLET: 'none' }}>
                <Link href={ROUTE.EXPERT_ORDERS({ orderId: order.id })}>
                  <Flex alignItems="center">
                    <Text c={COLOR_NAMES.PINEY_LAKE} fz={14} fw={500} mr={2}>
                      Details
                    </Text>
                    <Icon.ArrowRight size={16} color={COLOR_NAMES.PINEY_LAKE} />
                  </Flex>
                </Link>
              </Box>
            </Flex>
          </Flex>
          <Text
            fz={14}
            color={COLOR_NAMES.CARBON_COPY}
            display={{ base: 'none', sm: 'block' }}
            miw="200px"
          >
            {new Date(order.orderDate).toLocaleString('en-US')}
          </Text>
          <Box display={{ base: 'none', TABLET: 'block' }}>
            <Link href={ROUTE.EXPERT_ORDERS({ orderId: order.id })}>
              <Flex alignItems="center" minWidth={SPACE.PX_80}>
                <Text c={COLOR_NAMES.PINEY_LAKE} fz={14} fw={500} mr={2}>
                  Details
                </Text>
                <Icon.ArrowRight size={16} color={COLOR_NAMES.PINEY_LAKE} />
              </Flex>
            </Link>
          </Box>
        </Flex>
      </Accordion.Control>
      <Accordion.Panel
        p={{ base: '16px', md: '30px' }}
        pt={0}
        styles={{
          content: {
            padding: 0,
          },
        }}
      >
        <Grid
          gridTemplateColumns={{
            MOBILE: 'repeat(2, 1fr)',
            DESKTOP: 'repeat(4, 1fr)',
          }}
          mt={{ MOBILE: 2, TABLET: 10 }}
          gap={20}
          mb={{ MOBILE: 12, TABLET: 26 }}
        >
          <InfoRow
            label="Order Value"
            value={formatPrice({ price: order.orderValue })}
          />

          <InfoRow label="Total Items" value={order.totalItems} />
          <InfoRow
            label="Items with tracking"
            value={order.itemsWithTracking}
          />

          {/* <InfoRow label="Items with Tracking" value={order.itemsWithTracking} /> */}
          <InfoRow label="Ships To" value={order.shippingDestination} />
        </Grid>
        <DeliveryProgress value={order.deliveryProgress} />
        <Flex
          mt={24}
          gap={20}
          justifyContent="space-between"
          flexDirection={{ MOBILE: 'column', TABLET: 'row' }}
        >
          <Text fz={14} c={COLOR_NAMES.CARBON_COPY}>
            {order.itemsToShip} {order.itemsToShip === 1 ? 'item' : 'items'}{' '}
            remaining to ship
          </Text>

          <Text fz={14} c={COLOR_NAMES.CARBON_COPY}>
            {order.itemsToDeliver}{' '}
            {order.itemsToDeliver === 1 ? 'item' : 'items'} remaining to deliver
          </Text>
        </Flex>
      </Accordion.Panel>
    </Accordion.Item>
  )
}

export const RecentOrdersWidget = () => {
  const { data, isLoading } = ReactQuery.useGetExpertDashboardRecentOrders()

  const orders = data?.orders ?? []

  let content: React.ReactNode

  if (isLoading) {
    content = (
      <Box mt={20}>
        <RecentOrderRowSkeleton />
        <RecentOrderRowSkeleton />
      </Box>
    )
  } else if (orders.length === 0) {
    content = (
      <Flex
        mx="auto"
        p={SPACE.PX_30}
        maxWidth={LAYOUT_WRAPPER_MAX_WIDTH}
        flexDirection="column"
      >
        <NoData
          icon={Icon.Basket}
          header="Nothing to see here - yet!"
          content={`If you need help ordering furniture, The Expert team can help! Email ${EMAILS.TRADE_SUPPORT} to learn more.`}
        />

        <StyledLink
          href={ROUTE.SHOWROOM()}
          aria-label="Shop now"
          mt={SPACE.PX_20}
          width="50%"
          mx="auto"
        >
          Shop now
        </StyledLink>
      </Flex>
    )
  } else {
    content = (
      <>
        {orders.length > 0 && (
          <Box>
            <Accordion defaultValue={orders[0].id.toString()} chevron={null}>
              {orders.map((order) => (
                <RecentOrder key={order.id} order={order} />
              ))}
            </Accordion>
          </Box>
        )}
      </>
    )
  }

  return (
    <Card
      flexDirection="column"
      width="100%"
      height="100%"
      elevation={CARD_ELEVATION.NONE}
      hasBorders
      paddingSize={CARD_PADDING_SIZE.NONE}
      overflow="hidden"
    >
      <Box
        width="100%"
        borderRadius={SPACE.PX_8}
        height="100%"
        backgroundColor="white"
      >
        {isLoading ? (
          <RecentOrdersHeaderSkeleton />
        ) : (
          <Flex
            justifyContent="space-between"
            alignItems="center"
            p={{ MOBILE: SPACE.PX_16, TV: SPACE.PX_30 }}
            borderBottom={`1px solid ${COLOR_NAMES.JUPITER}`}
          >
            <Text fz={20} fw={500} c={COLOR_NAMES.EEIRE_BLACK} component="h2">
              Recent Orders
            </Text>
            <Link href={ROUTE.EXPERT_ORDERS()}>
              <Button variant={BUTTON_VARIANT.OUTLINE}>View all orders</Button>
            </Link>
          </Flex>
        )}

        {content}
      </Box>
    </Card>
  )
}
